import {
  ConditionBooleanOperators,
  ConditionOperators
} from '../../../../components/ConditionalLogic/conditionalTypes'
import { DsrRequestType } from '../../../../services/graphqlSchemaTypes'

export enum DsrWorkflowApiBlockTypes {
  DSR_ID_VERIFICATION = 'DSR_ID_VERIFICATION',
  DSR_REQUEST_APPROVAL = 'DSR_REQUEST_APPROVAL',
  DSR_DATA_DISCOVERY = 'DSR_DATA_DISCOVERY',
  DSR_REPORT_SHARING_AND_CLOSURE = 'DSR_REPORT_SHARING_AND_CLOSURE',
  CONDITIONAL = 'CONDITIONAL'
}

export enum EmailTemplatesTypes {
  REQUEST_SUBMITTED = 'requestSubmittedEmailTemplates',
  EMAIL_VERIFICATION_SUCCESSFUL = 'emailVerificationEmailTemplates',
  REQUEST_APPROVED = 'requestApprovalEmailTemplates',
  REPORT_SHARED = 'reportSharingEmailTemplates',
  REPORT_PASSWORD = 'reportPasswordEmailTemplates',
  REQUEST_CLOSED = 'closureEmailTemplates'
}

export enum DataDiscoveryActionTypes {
  AUTO_DISCOVER_AND_VALIDATE = 'AUTO_DISCOVER_AND_VALIDATE',
  AUTO_DISCOVER_AND_ERASE = 'AUTO_DISCOVER_AND_ERASE',
  AUTO_DISCOVER_AND_RECTIFY = 'AUTO_DISCOVER_AND_RECTIFY',
  MANUALLY_DISCOVER_AND_VALIDATE = 'MANUALLY_DISCOVER_AND_VALIDATE',
  DISCOVER_ONLY = 'DISCOVER_ONLY'
}

export enum DataDiscoveryActionTakenBy {
  REQUEST_OWNER = 'DSR_OWNER',
  DATA_SOURCE_OWNER = 'DATA_SOURCE_OWNER',
  SYSTEM = 'SYSTEM'
}

export type EmailIdVerification = {
  id?: string
  enabled?: boolean
  sendAcknowledgement: boolean
  requestSubmittedEmailTemplateId?: string
  emailVerificationSuccessEmailTemplateId?: string
  isNextStepAutomated?: boolean
  requestSubmittedEmailTemplateError?: boolean
  emailVerificationSuccessEmailTemplateError?: boolean
}

export type DsrWFDefaultDatasource = {
  id: string
  name: string
  type: string
  owner: string
  actionToBeTaken: ''
  actionToBeTakenBy: ''
}

export type stepRequestApproval = {
  id?: string
  enabled?: boolean
  approveRequest: boolean
  sendApprovalEmail: boolean
  requestApprovalEmailTemplateId?: string
  requestApprovalEmailTemplateError?: boolean
  sendAdditionalEmails?: boolean
  additionalEmailFields?: string[]
  additionalEmailTemplateId?: string
  additionalEmailTemplateError?: boolean
  additionalEmailFieldsError?: boolean
}

export type dsrWFDefaultDatasourceType = {
  id: string
  enabled?: boolean
  name: string
  type: string
  owner: string
  actionToBeTaken: DataDiscoveryActionTypes
  actionToBeTakenBy: DataDiscoveryActionTakenBy
  dsrErasureSupported?: boolean
  dsrRecitificationSupported?: boolean
  dsEnabled?: boolean
}

export type stepDataDiscoveryValidation = {
  id?: string
  enabled?: boolean
  dsrDefaultDatasources: dsrWFDefaultDatasourceType[]
}

export type stepReportGeneration = {
  id?: string
  enabled?: boolean
  shareReportWithDataSubject: boolean
  shareReportEmailTemplateId?: string
  reportPasswordEmailTemplateId?: string
  sendAdditionalEmailsForReportSharing?: boolean
  additionalEmailFieldsForReportSharing?: string[]
  additionalEmailFieldsForReportSharingError?: boolean
  automaticallyCloseRequest: boolean
  sendClosureEmail: boolean
  closureEmailTemplateId?: string
  closureEmailTemplateError?: boolean
  reportPasswordEmailTemplateError?: boolean
  reportSharingEmailTemplateError?: boolean
  sendAdditionalEmails?: boolean
  additionalEmailFields?: string[]
  additionalEmailTemplateId?: string
  additionalEmailTemplateError?: boolean
  additionalEmailFieldsError?: boolean
}

export enum BlockType {
  COND_BLOCK = 'cond_block',
  STEP_EMAIL_ID_VERIFICATION = 'stepEmailIdVerification',
  STEP_REQUEST_APPROVAL = 'stepRequestApproval',
  STEP_DATA_DISCOVERY_VALIDATION = 'stepDataDiscoveryValidation',
  STEP_REPORT_GENERATION = 'stepReportGeneration',
  START = 'START',
  FINISH = 'FINISH',
  ADD_CONDITION = 'ADD_CONDITION',
  CONNECTOR = 'CONNECTOR'
}

export interface Blocks {
  id?: string
  type?: BlockType
  enabled?: boolean
}

export type WorkflowConditionField = {
  field: string
  operator: ConditionOperators
  targetValues?: string[]
}

export type WorkflowConditionGroup = {
  operator: ConditionBooleanOperators
  conditions: WorkflowConditionField[]
}

export type WorkflowConditionalLogic = {
  operator: ConditionBooleanOperators
  groups: WorkflowConditionGroup[]
}

export type ConditionalFlow = {
  type: BlockType.COND_BLOCK
  data: WorkflowConditionalLogic
  blocks: Blocks[]
  id: string
}

export type Workflow = {
  workflowAutomationName?: string
  workflowAutomationNameError?: boolean
  workflowAutomationId?: string
  requestType?: DsrRequestType
  currenUserEmailId: string
  createdBy?: string
  createdAt?: string
  updateTimestamp?: string
  attachedDsrFormCount?: number

  stepEmailIdVerification: { [id: string]: EmailIdVerification }
  stepRequestApproval: { [id: string]: stepRequestApproval }
  stepDataDiscoveryValidation: { [id: string]: stepDataDiscoveryValidation }
  stepReportGeneration: { [id: string]: stepReportGeneration }
}

// export type Workflow = {
//   workflowAutomationName?: string
//   workflowAutomationNameError?: boolean
//   workflowAutomationId?: string
//   requestType?: DsrRequestType
//   currenUserEmailId: string
//   stepEmailIdVerification: EmailIdVerification
//   stepRequestApproval: stepRequestApproval
//   stepDataDiscoveryValidation: stepDataDiscoveryValidation
//   stepReportGeneration: stepReportGeneration
// }

export interface DSRFormDataItem {
  type: BlockType
  data?: ConditionalFlow | Blocks
}

export type DSRWorkflowStepDataType =
  | WorkflowConditionalLogic
  | EmailIdVerification
  | stepRequestApproval
  | stepDataDiscoveryValidation
  | stepReportGeneration

export type DSRFormData = DSRFormDataItem[]

export const initialDSRWorkflowState = {
  stepEmailIdVerification: {
    sendAcknowledgement: true,
    requestSubmittedEmailTemplateError: false,
    emailVerificationSuccessEmailTemplateError: false
  },
  stepRequestApproval: {
    approveRequest: false,
    sendApprovalEmail: false,
    requestApprovalEmailTemplateError: false
  },
  stepDataDiscoveryValidation: {
    dsrDefaultDatasources: []
  },
  stepReportGeneration: {
    shareReportWithDataSubject: false,
    automaticallyCloseRequest: false,
    sendClosureEmail: false,
    closureEmailTemplateError: false,
    reportPasswordEmailTemplateError: false,
    reportSharingEmailTemplateError: false
  }
}

export const initialDSRWorkflowStepData = {
  stepEmailIdVerification: {
    requestSubmittedEmailTemplates: [],
    emailVerificationEmailTemplates: []
  },
  stepRequestApproval: {
    requestApprovalEmailTemplates: []
  },
  stepDataDiscoveryValidation: {
    dsrDefaultDatasources: []
  },
  stepReportGeneration: {
    reportSharingEmailTemplates: [],
    reportPasswordEmailTemplates: [],
    closureEmailTemplates: []
  }
}
